import React, { useState, useEffect } from 'react';
import { CustomersProduct } from '@/models/customersTypes';
import { customersProductsApi } from '@/api/api';
import { showToast } from '@/components/UI/Toast';
import defaultImg from '@/public/img/imgicon.png';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingIcon from '@/components/UI/LoadingIcon';
import SeoHelmet from '@/components/UI/SeoHelmet';
import seoImg from '@/public/img/seo/produkte.png';
import { getUnitPriceLabel } from '@/helpers/productUnitPrice';

const CustomersProductDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [product, setProduct] = useState<CustomersProduct>();
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        if (!id) {
            navigate('/');
            return;
        }
        fetchProduct(Number(id));

    }, [id]);

    const fetchProduct = async (productId: number) => {
        try {
            const data = await customersProductsApi.getById(productId);
            setProduct(data);
        } catch (error) {
            showToast('Produkt konnte nicht geladen werden', 'error');
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (isLoading) return (<LoadingIcon text="Lade Produktdetails ..." />);

    if (!product) return <p className="text-center mt-20 text-red-500">Produkt nicht gefunden.</p>;

    const hasActiveDiscount: boolean =
        product.reduced_price !== null &&
        product.reduced_price !== undefined &&
        product.reduced_price < product.list_price;

    const discountPercent: number | null = hasActiveDiscount
        ? Math.round((1 - (product.reduced_price as number) / product.list_price) * 100)
        : null;

    const effectivePrice: number = hasActiveDiscount
        ? (product.reduced_price as number)
        : product.list_price;

    const displayUnit: string = product.uom === 'Einheit(en)' ? 'Stk' : product.uom;

    const unitPriceLabel: string | null = getUnitPriceLabel(
        effectivePrice,
        product.content_qty,
        product.uom
    );

    return (
        <>
            <SeoHelmet
                title="Yobsti – Aktionen & Angebote für Produkte des täglichen Bedarfs"
                description="Entdecken Sie die Produktdetails, wie Produzent, Verfügbarkeit,..."
                image={seoImg}
                url={`https://www.yobsti.de/produkte/${product.id}`}
            />

            <section className="flex-grow flex-col mx-auto w-full max-w-[1171px] mt-12 px-4 2xl:px-0">
                <div className="mt-8">
                    <button
                        onClick={handleBack}
                        className="bg-gray-200 text-gray-700 px-5 py-2 rounded-md hover:bg-gray-300 transition">
                        Zurück
                    </button>
                </div>

                <div className="max-w-5xl mx-auto px-4 py-12">
                    <div className="flex flex-col lg:flex-row gap-10 bg-white rounded-2xl shadow-md p-6">

                        {/* Bild */}
                        <div className="w-full lg:w-1/2 relative">
                            {discountPercent !== null && discountPercent > 0 && (
                                <span className="absolute top-2 right-2 bg-red text-white text-md font-bold px-2 py-1 rounded-full shadow">
                                    -{discountPercent}%
                                </span>
                            )}
                            <img
                                src={product.image_url || defaultImg}
                                alt={product.name}
                                loading="lazy"
                                className="w-full h-70 object-contain rounded"
                                style={{ maxHeight: '50vh' }}
                                onError={(e) => { e.currentTarget.src = defaultImg; e.currentTarget.onerror = null; }}
                            />
                        </div>

                        {/* Details */}
                        <div className="flex flex-col gap-4 w-full lg:w-1/2">
                            <h1 className="text-3xl font-bold text-primary">{product.name}</h1>

                            <div className="text-sm text-gray-500 space-y-1">
                                <p><strong>Produzent:</strong> {product.producer}</p>
                                <p><strong>Händler:</strong> {product.vendor}</p>
                                <p><strong>Kategorie:</strong> {product.category?.name}</p>
                                <p><strong>Einheit:</strong> {product.content_qty} {displayUnit}</p>
                            </div>

                            {/* Preise */}
                            <div className="mt-4">
                                {hasActiveDiscount ? (
                                    <div className="flex items-baseline gap-3">
                                        <p className="text-lg text-gray-500 line-through">{product.list_price.toFixed(2)} €</p>
                                        <p className="text-2xl font-bold text-secondary">{(product.reduced_price as number).toFixed(2)} €</p>
                                    </div>
                                ) : (
                                    <p className="text-2xl font-bold text-lineGreen">{product.list_price.toFixed(2)} €</p>
                                )}
                                {unitPriceLabel && (
                                    <p className="mt-1 text-sm text-gray-500">{unitPriceLabel}</p>
                                )}
                            </div>

                            {/* Tags */}
                            {product.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-4">
                                {product.tags.map((tag) => (
                                    <span key={tag} className="bg-secondary text-white text-sm px-3 py-1 rounded-full">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default CustomersProductDetails;
